import { useCallback, useMemo } from 'react';
import { TherapySessionType } from '../types';
import { useCustomerDetailsQuery } from '@upheal/billing/hooks/useCustomerDetailsQuery';
import { isAdvancedNoteType } from '../utils/billing/featureLockingUtils';
import { FF_FEATURE_LOCKING } from '@upheal/utils/feature-flags';
export enum SubscriptionFeatureId {
    ADVANCED_NOTE = 'advanced-note',
    FAMILY_NOTE = 'family-note',
    ADVANCED_ANALYTICS = 'advanced-analytics',
    TREATMENT_PLANS = 'treatment-plans',
    TREATMENT_SUMMARY = 'treatment-summary',
    CLIENT_SUMMARIES = 'client-summaries',
    ADVANCED_INSIGHTS = 'advanced-insights',

    // Free plan features limitations
    PRIORITY_SUPPORT = 'priority-support',
    STANDARD_SUPPORT = 'standard-support',
    TRANSCRIPT_AUTO_DELETION = 'transcript-auto-deletion',
    DATA_PREFERENCES = 'data-preferences',
    AUDIO_RECORDER = 'audio-recorder',
    EXTENSION_RECORDER = 'extension-recorder',
    CUSTOMIZATION = 'customization',
    ADVANCED_NOTE_UPHEAL = 'advanced-note-upheal',
    UPHEAL_AI_VIDEO_CALLS = 'upheal-ai-video-calls',
    ZOOM_INTEGRATION = 'zoom-integration',
    CUSTOM_TEMPLATES = 'custom-templates',
    SMART_EDIT = 'smart-edit',
    CUSTOM_PROMPT_SECTION_UNLIMITED = 'custom-prompt-section-unlimited',
    COMPLIANCE_CHECKER = 'compliance-checker',
    SELF_SCHEDULING = 'self-scheduling',
    DIAGNOSIS = 'diagnosis',
    CHART_MIGRATION = 'chart-migration',
    SSO = 'sso',
    PRACTICE_FORMS = 'practice-forms',
    PAYMENTS = 'client-payments',
}

// The configuration for the Smart Edit lock is incorrect, so we will reuse the one for custom templates.
export const SMART_EDIT_LOCK_HOTFIX = SubscriptionFeatureId.CUSTOM_TEMPLATES;

type Props = {
    isDemoMode?: boolean;
};

export const useCustomerDetails = ({ isDemoMode = false }: Props = {}) => {
    const {
        data,
        loading: loadingCustomerDetails,
        error: customerDetailsError,
        refetch: refetchCustomerDetails,
    } = useCustomerDetailsQuery();

    const features = useMemo(
        () => data?.getCustomerDetails?.subscription?.features || [],
        [data],
    );

    const isFeatureEnabled = useCallback(
        (featureId: SubscriptionFeatureId) => {
            if (isDemoMode) {
                return true;
            }
            if (features.length === 0) {
                return true; // default to enabled if no features are loaded or if the client is a demo client
            }
            const feature = features.find((f) => f.id === featureId);
            if (!feature) {
                return true; // default to enabled if feature is not found
            }
            return feature.enabled;
        },
        [features, isDemoMode],
    );

    const subscription = data?.getCustomerDetails?.subscription;
    const trial = data?.getCustomerDetails?.subscription?.trial;
    const customTrial = data?.getCustomerDetails?.subscription?.customTrial;
    const pendingPayment =
        data?.getCustomerDetails?.subscription?.pendingPayment;
    const pendingCancellation =
        data?.getCustomerDetails?.subscription?.pendingCancellation;

    const isPartOfUnlimitedRemovalExperiment = Boolean(
        subscription?.experiments?.some(
            (experiment) => experiment === 'unlimited-removal',
        ),
    );

    const featuresEnabled = useMemo(() => {
        return {
            advancedAnalyticsEnabled: isFeatureEnabled(
                SubscriptionFeatureId.ADVANCED_ANALYTICS,
            ),
            advancedInsightsEnabled: isFeatureEnabled(
                SubscriptionFeatureId.ADVANCED_INSIGHTS,
            ),
            zoomIntegrationEnabled: isFeatureEnabled(
                SubscriptionFeatureId.ZOOM_INTEGRATION,
            ),
            transcriptAutoDeletionEnabled:
                FF_FEATURE_LOCKING.off ||
                isFeatureEnabled(
                    SubscriptionFeatureId.TRANSCRIPT_AUTO_DELETION,
                ),
            treatmentPlansEnabled: isFeatureEnabled(
                SubscriptionFeatureId.TREATMENT_PLANS,
            ),
            audioRecorderEnabled: isFeatureEnabled(
                SubscriptionFeatureId.AUDIO_RECORDER,
            ),
            uphealVideoCallsEnabled: isFeatureEnabled(
                SubscriptionFeatureId.UPHEAL_AI_VIDEO_CALLS,
            ),
            dataPreferencesEnabled: isFeatureEnabled(
                SubscriptionFeatureId.DATA_PREFERENCES,
            ),
            clientSummariesEnabled: isFeatureEnabled(
                SubscriptionFeatureId.CLIENT_SUMMARIES,
            ),
            extensionRecorderEnabled: isFeatureEnabled(
                SubscriptionFeatureId.EXTENSION_RECORDER,
            ),
            customizationEnabled:
                FF_FEATURE_LOCKING.off ||
                isFeatureEnabled(SubscriptionFeatureId.CUSTOMIZATION),
            advancedNoteUphealEnabled:
                FF_FEATURE_LOCKING.off ||
                isFeatureEnabled(SubscriptionFeatureId.ADVANCED_NOTE_UPHEAL),
            familyNoteEnabled: isFeatureEnabled(
                SubscriptionFeatureId.FAMILY_NOTE,
            ),
            smartEditEnabled: isFeatureEnabled(SMART_EDIT_LOCK_HOTFIX),
        };
    }, [isFeatureEnabled]);

    return {
        loadingCustomerDetails,
        customerDetailsError,
        refetchCustomerDetails,
        planId: subscription?.planId,
        features: features,
        planName: subscription?.planName,
        licenseCount: subscription?.licenseCount,
        billingPeriod: subscription?.billingPeriod,
        isFeatureEnabled,
        ...featuresEnabled,
        noteTypeEnabled: useCallback(
            (noteType: TherapySessionType) => {
                return (
                    !isAdvancedNoteType(noteType) ||
                    isFeatureEnabled(SubscriptionFeatureId.ADVANCED_NOTE)
                );
            },
            [isFeatureEnabled],
        ),
        trial,
        customTrial,
        pendingPayment,
        pendingCancellation,
        hasPaymentMethod: subscription?.hasPaymentMethod,
        isPartOfUnlimitedRemovalExperiment,
    };
};
